import { leagueFetch } from "@leagueplatform/league-fetch";
import {
  getUserProfile,
  setUserProfile,
} from "@leagueplatform/user-profile-api";
import { toastActions, TOAST_STATUS } from "@leagueplatform/toast-messages";
import type { MasonryEngineActionHandler } from "@leagueplatform/masonry-engine";

/**
 * Quest custom Masonry action handler for `downloadPhysicianForm`.
 *
 * Surfaced by the `screenings$selfScreening$downloadForm$root` node's
 * "Download Form" CTA. The BE-side action ships with three payload
 * fields (`documentId`, `documentType`, `planYearEndDate`); on click we:
 *
 *   1. Fetch the PDF from `/v2/document-content/{documentId}` (League
 *      member auth is added by `leagueFetch`).
 *   2. Trigger a browser-native download via a programmatic anchor.
 *   3. Write `{planYearEndDate}-physician-form-downloaded = "true"` into
 *      the user's `custom_fields` so the BE resolver renders the
 *      "downloaded" state on subsequent loads — mirrors Android's
 *      `UpdatePhysicianFormDownloadedUseCase` (quest-android#94).
 *
 * Any failure surfaces an error toast (`STR_DOWNLOAD_FORM_ERROR`) using
 * the same `toastActions` integration the form-container uses for
 * profile-save failures. There is no success UI — the browser's native
 * download bar provides immediate feedback and the BE re-renders the
 * page state on next load.
 */

const DOCUMENT_CONTENT_API = "/v2/document-content";
const DOWNLOADED_CUSTOM_FIELD_SUFFIX = "physician-form-downloaded";
const DOWNLOADED_CUSTOM_FIELD_VALUE = "true";

type DownloadPhysicianFormPayload = {
  documentId: string;
  documentType: string;
  planYearEndDate: string;
};

type CustomField = { name: string; value: string };

const isCompletePayload = (
  payload: unknown
): payload is DownloadPhysicianFormPayload => {
  if (typeof payload !== "object" || payload === null) return false;
  const candidate = payload as Record<string, unknown>;
  return (
    typeof candidate.documentId === "string" &&
    candidate.documentId.length > 0 &&
    typeof candidate.documentType === "string" &&
    candidate.documentType.length > 0 &&
    typeof candidate.planYearEndDate === "string" &&
    candidate.planYearEndDate.length > 0
  );
};

const formatDownloadFilename = (planYearEndDate: string): string => {
  const now = new Date();
  const pad = (value: number) => String(value).padStart(2, "0");
  const timestamp = `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(
    now.getDate()
  )}-${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}`;
  return `physician-form-${planYearEndDate}-${timestamp}.pdf`;
};

const triggerBrowserDownload = (blob: Blob, filename: string): void => {
  const objectUrl = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = objectUrl;
  anchor.download = filename;
  anchor.rel = "noopener";
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(objectUrl);
};

const markPhysicianFormDownloaded = async (
  planYearEndDate: string
): Promise<void> => {
  const profile = (await getUserProfile()) as unknown as {
    custom_fields?: CustomField[];
  };
  const fieldName = `${planYearEndDate}-${DOWNLOADED_CUSTOM_FIELD_SUFFIX}`;
  const existingFields = profile?.custom_fields ?? [];
  const preservedFields = existingFields.filter(
    (field) => field.name !== fieldName
  );
  const updatedFields: CustomField[] = [
    ...preservedFields,
    { name: fieldName, value: DOWNLOADED_CUSTOM_FIELD_VALUE },
  ];

  await setUserProfile({ custom_fields: updatedFields } as never);
};

export const QuestDownloadPhysicianFormActionHandler: MasonryEngineActionHandler<
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  any,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  any
> = async (action, resources) => {
  const showErrorToast = () => {
    toastActions.add({
      type: TOAST_STATUS.ERROR,
      text: resources.intl.formatMessage({ id: "STR_DOWNLOAD_FORM_ERROR" }),
    });
  };

  try {
    if (!isCompletePayload(action?.payload)) {
      showErrorToast();
      return;
    }

    const { documentId, documentType, planYearEndDate } = action.payload;

    const response = await leagueFetch(
      `${DOCUMENT_CONTENT_API}/${encodeURIComponent(
        documentId
      )}?document_type=${encodeURIComponent(documentType)}`
    );

    if (!response?.ok) {
      showErrorToast();
      return;
    }

    const blob = await response.blob();
    triggerBrowserDownload(blob, formatDownloadFilename(planYearEndDate));

    await markPhysicianFormDownloaded(planYearEndDate);
  } catch (error) {
    showErrorToast();
  }
};
